import { useState, useCallback } from 'react'
import * as R from 'remeda'
import { nanoid } from 'nanoid'
import invariant from 'tiny-invariant'
import { DistributedOmit } from 'type-fest'
import { captureException } from '@chilipiper/sentry'
import { PublishCallback } from '../chat-data/observableResource'
import { Affiliations, Marker, Room, RoomId } from '../chat-data/types'
import { useChatDataState } from '../chat-data/useChatDataState'
import { exponentialSleep } from '../helpers/exponentialSleep'
import { getLastEventWithPreview } from '../helpers/getLastEventWithPreview'
import { createClient, Credentials, EventStatus, ConnectionStatus } from './XMPPClient'
import { PayloadIn, TypedEnvelopeOut } from './types'
import { DEFAULT_MAX_ROOMS_INBOX_CHUNK } from './constants'

export type ClientEvents = {
  event: {
    data: {
      client: 'private' | 'public'
      isUnread: boolean
      payload: PayloadIn
      /**
       * If true, the event is from a MAM request. This means it's not a new event, but an event
       * that was sent before and retreived when looking up history
       */
      isMam: boolean
    }
  }
  roomRead: { data: RoomId }
}

export const useXMPPClient = (
  clientId: 'private' | 'public',
  publish: PublishCallback<ClientEvents>
) => {
  const [client] = useState(() => createClient(clientId))
  const [userId, setUserId] = useState<string>()
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('DISCONNECTED')
  const {
    events: chatDataEvents,
    isMarked,
    roomAssignees,
    registerEvent,
    resetAffiliations,
  } = useChatDataState()

  /** Stores the event in memory and publishes it for external subscribers */
  const registerAndPublishEvent = useCallback(
    (
      event: Parameters<typeof registerEvent>[0],
      publishMetadata: Omit<ClientEvents['event']['data'], 'payload' | 'client'>
    ) => {
      registerEvent(event, eventItem => {
        if (eventItem.type === 'Marker') {
          return
        }
        publish('event', {
          client: clientId,
          payload: eventItem,
          isUnread: publishMetadata.isUnread,
          isMam: publishMetadata.isMam,
        })
      })
    },
    [clientId, publish, registerEvent]
  )

  /** Starts the XMPP client with provided credentials and sets up listeners */
  const start = useCallback(
    async (
      tenantId: string,
      workspaceId: string | undefined,
      getConfig: (isRetrying: boolean) => Promise<{
        credentials: Credentials
      }>
    ) => {
      setConnectionStatus('CONNECTING')
      await tryWithExponentialBackoff(
        async retry => {
          const { credentials } = await getConfig(retry === 0)
          client.auth(credentials, tenantId)
          client.onConnectionStatusChange(value => {
            setConnectionStatus(value)
          })

          client.setupListeners({
            onConversationStatusChange: event => {
              registerAndPublishEvent(event, {
                isUnread: true,
                isMam: false,
              })
            },
            onGroupchatMessageFailed: e => {
              registerAndPublishEvent(e, {
                isUnread: true,
                isMam: false,
              })
            },
            onGroupchatMessage: e => {
              registerAndPublishEvent(e, {
                isUnread: true,
                isMam: false,
              })
            },
            onGroupchatMarker: e => registerEvent(e),
            onAffiliationChange: e => registerEvent(e),
            onArchivedAffiliationsChange: e => registerEvent(e),
          })
          await client.start().catch(error => {
            console.error('Error: ', error)
            throw error
          })
          setUserId(credentials.username.split('@')[0])
        },
        CONNECTION_RETRIES,
        250
      ).then(
        () => {
          client.onError((error, stopClient) => {
            console.error('Error on XMPPClient, trying to reconnect', error)
            stopClient()
              .then(R.doNothing, R.doNothing)
              .then(() => start(tenantId, workspaceId, getConfig))
              .then(R.doNothing, R.doNothing)
          })
          setConnectionStatus('CONNECTED')
        },
        () => {
          setConnectionStatus('ERROR')
        }
      )
    },
    [client, registerAndPublishEvent, registerEvent]
  )

  const stop = useCallback(async () => {
    await client.stop()
    setConnectionStatus('DISCONNECTED')
  }, [client])

  const sendEvent = useCallback(
    async (
      toRoomId: string,
      event: DistributedOmit<TypedEnvelopeOut, 'envelopeId'>,
      optimisticUpdate?: boolean
    ) => {
      let eventToSend: TypedEnvelopeOut
      if (event.type === 'BotEnvelope') {
        const lastEnvelopeId =
          R.pipe(
            chatDataEvents[toRoomId] ?? {},
            R.values(),
            R.filter(e => e?.author !== userId),
            R.sortBy(e => e?.timestamp ?? ''),
            R.findLast(e => Boolean(e && 'envelopeId' in e)),
            e => (e && 'envelopeId' in e ? e.envelopeId : 0)
          ) ?? 0
        eventToSend = {
          ...event,
          envelopeId: lastEnvelopeId + 1,
        }
      } else {
        eventToSend = event
      }
      let id = nanoid()
      if (optimisticUpdate) {
        invariant(userId, 'userId must be defined to convert payloadOut to payloadIn')
        const optimisticPayload = {
          ...eventToSend.payload,
          author: userId,
          sent: false,
          id: nanoid(),
          roomId: toRoomId,
          statuses: {},
          timestamp: new Date().toISOString(),
        }
        registerAndPublishEvent(optimisticPayload, {
          isUnread: false,
          isMam: false,
        })
        if (optimisticPayload.id) {
          id = optimisticPayload.id
        }
      }
      await client.sendEvent(toRoomId, eventToSend, id).catch(error => {
        console.error(`Error: ${error} - Failed to send event: ${JSON.stringify(eventToSend)}`)
      })
      return id
    },
    [client, registerAndPublishEvent, userId, chatDataEvents]
  )

  const markEvent = useCallback(
    async (to: string, eventId: string, status: EventStatus) => {
      const id = nanoid()
      await client.markEvent(to, eventId, status, id).catch(error => {
        console.error(`Error: ${error} - Failed to mark event: ${eventId}`)
      })
      invariant(userId, 'userId must be defined to mark event')
      registerEvent({ type: 'Marker', author: userId, roomId: to, eventId, status, id })
      const event = chatDataEvents[to]?.[eventId]
      if (event && event.type !== 'Marker') {
        registerAndPublishEvent(event, {
          isUnread: false,
          isMam: false,
        })
      }
    },
    [client, userId, registerEvent, chatDataEvents, registerAndPublishEvent]
  )

  const loadRoomEvents = useCallback(
    async (
      roomId: string,
      params: {
        before?: string
        max: number
        waitForRoomPreparation?: boolean
      }
    ) => {
      const events = await client.loadRoomEvents(roomId, params)
      const markersArray = events.filter((e): e is Marker => e.type === 'Marker')
      const markersByEventId = R.indexBy(markersArray, event => event.eventId)
      for (const event of events) {
        registerEvent(event)
      }

      events
        .filter(
          (e): e is Exclude<typeof e, Marker | Affiliations> =>
            e.type !== 'Marker' && e.type !== 'Affiliations'
        )
        .forEach(event => {
          if (event.id) {
            const marker = markersByEventId[event.id]
            delete markersByEventId[event.id]
            return registerAndPublishEvent(event, { isUnread: !marker, isMam: true })
          } else {
            return registerAndPublishEvent(event, { isUnread: true, isMam: true })
          }
        })

      markersArray
        .map(marker => chatDataEvents[roomId]?.[marker.eventId])
        .forEach(payload => {
          if (payload && payload.type !== 'Marker') {
            registerAndPublishEvent(payload, { isUnread: false, isMam: true })
          }
        })
      return events
    },
    [client, registerEvent, registerAndPublishEvent, chatDataEvents]
  )

  /**
   * Starting from the latest gradually loads more events for a room until an event with preview is
   * found
   */
  const loadLastEventsForRoom = useCallback(
    async (room: Pick<Room, 'id' | 'lastEvents'>) => {
      if (room.lastEvents && getLastEventWithPreview(room.lastEvents)) {
        return room.lastEvents
      }
      const eventsToLoad = 5
      const events = await client.loadRoomEvents(room.id, { max: eventsToLoad })
      if (events.length < eventsToLoad || getLastEventWithPreview(events)) {
        return events
      }
      const moreEvents = await client.loadRoomEvents(room.id, { max: 50 })
      if (!getLastEventWithPreview(moreEvents)) {
        console.error(
          `Unimplemented event preview roomId: "${room.id}", events: ${JSON.stringify(moreEvents)}`
        )
      }
      return moreEvents
    },
    [client]
  )

  /**
   * Starting from the latest gradually loads more events for each room until an event with preview
   * is found for each room
   */
  const loadLastEventsForRooms = useCallback(
    async (rooms: Pick<Room, 'id' | 'lastEvents'>[]) => {
      const events = await Promise.allSettled(rooms.map(room => loadLastEventsForRoom(room)))
      return R.pipe(
        events,
        R.zip(rooms),
        R.map(
          ([events, room]) =>
            [
              room.id,
              events.status === 'fulfilled'
                ? { type: 'success' as const, value: events.value }
                : { type: 'error' as const, error: events.reason },
            ] as const
        ),
        R.fromEntries()
      )
    },
    [loadLastEventsForRoom]
  )

  const loadInbox = useCallback(
    async (box: 'inbox' | 'archive' | 'all' = 'inbox', after?: string) => {
      try {
        const rooms = await client.loadInbox({ box, max: DEFAULT_MAX_ROOMS_INBOX_CHUNK, after })
        const eventsByRoomId = await loadLastEventsForRooms(rooms)
        return rooms.map(room => {
          const roomEventsResult = eventsByRoomId[room.id]
          if (!roomEventsResult || roomEventsResult.type === 'error') {
            if (
              roomEventsResult?.error instanceof Error &&
              (roomEventsResult.error.message.startsWith('not-allowed') ||
                roomEventsResult.error.message.startsWith('item-not-found'))
            ) {
              return { ...room, isDisabled: true }
            } else {
              captureException(
                new Error(`Error loading events for room "${room.id}": ${roomEventsResult?.error}`)
              )
            }
            return room
          }
          const events = roomEventsResult.value ?? []
          if (events.length === 0) {
            captureException(
              new Error(`A room "${room.id}" was found, but there were no events in it`)
            )
          }
          /**
           * Add from the end so markers are processed before events so we can instantly know if
           * event is unread or not when adding it
           */
          for (const event of events.toReversed()) {
            if (event.type !== 'Marker' && event.type !== 'Affiliations') {
              registerAndPublishEvent(event, {
                isUnread: !isMarked(room.id, event),
                isMam: true,
              })
            } else {
              registerEvent(event)
            }
          }
          return {
            ...room,
            lastEvents: events,
          }
        })
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [client, loadLastEventsForRooms, registerAndPublishEvent, isMarked, registerEvent]
  )

  const loadRoomDetails = useCallback(
    async (roomId: string) => {
      try {
        return await client.loadInboxItem(roomId)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [client]
  )

  const loadAffiliations = useCallback(
    async ({ roomId }: { roomId: string }) => {
      try {
        const affiliations = await client.getAffiliations(roomId)
        resetAffiliations(roomId, affiliations.users)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [client, resetAffiliations]
  )

  return {
    userId,
    start,
    stop,
    status: connectionStatus,
    sendEvent,
    markEvent,
    loadRoomEvents,
    loadInbox,
    loadRoomDetails,
    events: chatDataEvents,
    registerEvent,
    loadAffiliations,
    roomAssignees,
    setMuteStatus: client.setMuteStatus,
  }
}

/** Tries to execute a function with exponential backoff */
const tryWithExponentialBackoff = async <T>(
  fn: (tryNumber: number) => Promise<T>,
  maxRetries: number,
  backoffBase: number
): Promise<T> => {
  const executeTry = async (retry: number): Promise<T> => {
    try {
      return await fn(retry)
    } catch (error) {
      if (retry >= maxRetries) {
        throw error
      }
      await exponentialSleep(retry, backoffBase)
      return executeTry(retry + 1)
    }
  }
  return executeTry(0)
}

const CONNECTION_RETRIES = 7
